'use client';

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { AlertCircle, Copy, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { format, parse } from 'date-fns';
import type { StudentProfile } from '@court-sync/shared';
import { areCampDaysFullyBooked, isCampFullyBookedByDay } from '@court-sync/shared';
import { lessonEnrollmentService } from '@/services/lessonEnrollment';
import { academyStudentService } from '@/services/academyStudentService';

/** When opening enroll from a calendar occurrence (owner session detail). */
export type CalendarEnrollContext = {
  kind: 'term_group' | 'camp';
  sessionDateYmd: string;
  sessionId?: string;
};

interface EnrollStudentModalProps {
  lessonId: string;
  lessonName: string;
  /** Program and/or age group label when the lesson has one (e.g. camp variant). */
  lessonTrackLabel?: string | null;
  /** Required for term group / term-bound private staff enroll; omit for camps and standalone private. */
  termId?: string;
  academyId: string;
  isOpen: boolean;
  onClose: () => void;
  onEnrollSuccess?: () => void;
  /** Set from calendar session detail so the owner can choose session-only vs full term / full camp. */
  calendarEnrollmentContext?: CalendarEnrollContext | null;
  /** Render inside a parent panel (e.g. post-create wizard) instead of a nested modal. */
  presentation?: 'modal' | 'inline';
  /** Public booking URL for standalone private lessons; shown with copy action when set. */
  shareBookingLink?: string;
  /** Inline mode: hide in-tab Cancel; pin actions to drawer footer or sticky bar. */
  hideInlineCancel?: boolean;
  /** When set with inline + hideInlineCancel, renders actions into the parent Drawer footer. */
  onInlineFooterChange?: (footer: React.ReactNode | null) => void;
  /** When set with enrolledCount, blocks enroll when at capacity. */
  maxCapacity?: number;
  enrolledCount?: number;
  /** Per-day enrollment counts for camp capacity checks (YYYY-MM-DD → count). */
  campEnrollmentByDay?: Record<string, number>;
  /** Keep panel open after enroll (e.g. add multiple students post-create). */
  stayOpenAfterEnroll?: boolean;
  /** Camp management: YYYY-MM-DD days for this week/instance (enables full week vs selected days). */
  campSessionDates?: string[];
  /** While camp session dates are loading from the server. */
  campSessionDatesLoading?: boolean;
  /** When false with calendar context, session/day-only scope is disabled (no per-session list price). */
  canEnrollSingleSession?: boolean;
  /** Show a non-blocking warning when staff enrolls into a past session or camp. */
  showPastEnrollmentWarning?: boolean;
}

export function EnrollStudentModal({
  lessonId,
  lessonName,
  lessonTrackLabel = null,
  termId,
  academyId,
  isOpen,
  onClose,
  onEnrollSuccess,
  calendarEnrollmentContext = null,
  presentation = 'modal',
  shareBookingLink,
  hideInlineCancel = false,
  onInlineFooterChange,
  maxCapacity,
  enrolledCount = 0,
  campEnrollmentByDay,
  stayOpenAfterEnroll = false,
  campSessionDates,
  campSessionDatesLoading = false,
  canEnrollSingleSession = true,
  showPastEnrollmentWarning = false,
}: EnrollStudentModalProps) {
  const [activeTab, setActiveTab] = useState<'existing' | 'new'>('existing');
  const scopeResetKey = isOpen
    ? `${campSessionDates?.join(',') ?? ''}:${calendarEnrollmentContext?.sessionDateYmd ?? 'none'}:${canEnrollSingleSession}`
    : 'closed';
  const defaultEnrollmentScope =
    calendarEnrollmentContext && canEnrollSingleSession ? 'session' : 'full';
  const [enrollmentScope, setEnrollmentScope] = useState<'full' | 'session'>(() => defaultEnrollmentScope);
  const [campBookingMode, setCampBookingMode] = useState<'full_week' | 'selected_days'>('full_week');
  const [selectedCampDays, setSelectedCampDays] = useState<Set<string>>(() => new Set(campSessionDates ?? []));
  const [scopeResetKeySeen, setScopeResetKeySeen] = useState(scopeResetKey);

  if (isOpen && scopeResetKey !== scopeResetKeySeen) {
    setScopeResetKeySeen(scopeResetKey);
    setSelectedCampDays(new Set(campSessionDates ?? []));
    setCampBookingMode('full_week');
    setEnrollmentScope(defaultEnrollmentScope);
  }

  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedStudentLabel, setSelectedStudentLabel] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [studentSearchDebounced, setStudentSearchDebounced] = useState('');
  const [searchResults, setSearchResults] = useState<StudentProfile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  const [newStudentFirstName, setNewStudentFirstName] = useState('');
  const [newStudentLastName, setNewStudentLastName] = useState('');
  const [newStudentEmail, setNewStudentEmail] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendBookingConfirmation, setSendBookingConfirmation] = useState(true);

  const atCapacity = (() => {
    if (maxCapacity == null || maxCapacity <= 0 || maxCapacity === -1) return false;
    if (campSessionDates?.length && campEnrollmentByDay) {
      if (campBookingMode === 'full_week') {
        return isCampFullyBookedByDay(campSessionDates, campEnrollmentByDay, maxCapacity);
      }
      if (selectedCampDays.size === 0) return false;
      return areCampDaysFullyBooked([...selectedCampDays], campEnrollmentByDay, maxCapacity);
    }
    return enrolledCount >= maxCapacity;
  })();

  const searchLoadingVisible =
    studentSearchDebounced.length >= 1 && searchLoading;

  useEffect(() => {
    const t = setTimeout(() => setStudentSearchDebounced(studentSearchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [studentSearchQuery]);

  type EnrollScopePayload = {
    enrollmentScope?: 'full' | 'session';
    sessionDate?: string;
    sessionId?: string;
  };

  const buildEnrollScopePayloads = useCallback((): EnrollScopePayload[] => {
    if (calendarEnrollmentContext) {
      if (enrollmentScope === 'full') {
        return [{ enrollmentScope: 'full' }];
      }
      return [
        {
          enrollmentScope: 'session',
          sessionDate: calendarEnrollmentContext.sessionDateYmd,
          ...(calendarEnrollmentContext.sessionId
            ? { sessionId: calendarEnrollmentContext.sessionId }
            : {}),
        },
      ];
    }
    if (campSessionDates?.length) {
      if (campBookingMode === 'full_week') {
        return [{ enrollmentScope: 'full' }];
      }
      const days = [...selectedCampDays].sort();
      return days.map((sessionDate) => ({ enrollmentScope: 'session' as const, sessionDate }));
    }
    return [{}];
  }, [
    calendarEnrollmentContext,
    campSessionDates,
    campBookingMode,
    selectedCampDays,
    enrollmentScope,
  ]);

  const enrollStudentWithScopes = useCallback(
    async (
      studentId: string,
      options: { sendBookingConfirmation: boolean }
    ): Promise<{ success: boolean; error?: string }> => {
      const scopes = buildEnrollScopePayloads();
      if (campSessionDates?.length && scopes.length === 0) {
        return { success: false, error: 'Please select at least one camp day' };
      }
      for (const scope of scopes) {
        const result = await lessonEnrollmentService.enrollStudent(lessonId, {
          studentId,
          academyId,
          ...(termId ? { termId } : {}),
          ...scope,
          sendBookingConfirmation: options.sendBookingConfirmation,
        });
        if (!result.success) {
          return { success: false, error: result.error || 'Failed to enroll student' };
        }
      }
      return { success: true };
    },
    [academyId, buildEnrollScopePayloads, campSessionDates, lessonId, termId]
  );

  const toggleCampDay = useCallback((dateStr: string) => {
    setSelectedCampDays((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  }, []);

  const selectableCampDays = campSessionDates ?? [];
  const allCampDaysSelected =
    selectableCampDays.length > 0 && selectableCampDays.every((d) => selectedCampDays.has(d));

  const formatCampDayLabel = (ymd: string): string => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
    return format(parse(ymd, 'yyyy-MM-dd', new Date()), 'EEE d MMM yyyy');
  };

  useEffect(() => {
    if (!isOpen || !academyId || studentSearchDebounced.length < 1) {
      return undefined;
    }
    let cancelled = false;
    const query = studentSearchDebounced;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setSearchLoading(true);
      academyStudentService
        .getAcademyStudentsPaginated(academyId, 1, 25, {
          searchQuery: query,
          isActive: true,
        })
        .then((res) => {
          if (!cancelled) setSearchResults(res.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearchLoading(false);
        });
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, academyId, studentSearchDebounced]);

  useEffect(() => {
    if (!resultsOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      const el = searchWrapRef.current;
      if (el && !el.contains(e.target as Node)) setResultsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [resultsOpen]);

  const pickStudent = useCallback((s: StudentProfile) => {
    setSelectedStudentId(s.id);
    setSelectedStudentLabel(`${s.firstName} ${s.lastName}`);
    setStudentSearchQuery(`${s.firstName} ${s.lastName}`);
    setResultsOpen(false);
    setError(null);
  }, []);

  const handleEnrollExisting = async () => {
    if (!selectedStudentId) {
      setError('Please select a student');
      return;
    }

    setIsEnrolling(true);
    setError(null);

    const result = await enrollStudentWithScopes(selectedStudentId, {
      sendBookingConfirmation,
    });

    if (result.success) {
      setSelectedStudentId('');
      setSelectedStudentLabel('');
      setStudentSearchQuery('');
      onEnrollSuccess?.();
      if (stayOpenAfterEnroll) {
        toast.success('Student enrolled');
      } else {
        onClose();
      }
    } else {
      setError(result.error || 'Failed to enroll student');
    }

    setIsEnrolling(false);
  };

  const handleEnrollNew = async () => {
    if (!newStudentFirstName || !newStudentLastName) {
      setError('Please enter first and last name');
      return;
    }

    setIsEnrolling(true);
    setError(null);

    try {
      const emailTrim = newStudentEmail.trim();
      const createRes = await academyStudentService.createManualStudent(academyId, {
        firstName: newStudentFirstName.trim(),
        lastName: newStudentLastName.trim(),
        gender: 'other',
        inviteEmail: emailTrim || undefined,
      });
      const newId = createRes.success && createRes.data?.id ? createRes.data.id : null;
      if (!newId) {
        setError(createRes.error || 'Failed to create student');
        setIsEnrolling(false);
        return;
      }
      const enrollResult = await enrollStudentWithScopes(newId, {
        sendBookingConfirmation: emailTrim ? sendBookingConfirmation : false,
      });
      if (enrollResult.success) {
        setNewStudentFirstName('');
        setNewStudentLastName('');
        setNewStudentEmail('');
        onEnrollSuccess?.();
        if (stayOpenAfterEnroll) {
          toast.success('Student created and enrolled');
        } else {
          onClose();
        }
      } else {
        setError(enrollResult.error || 'Failed to enroll new student');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create or enroll');
    }

    setIsEnrolling(false);
  };

  const copyShareBookingLink = async () => {
    if (!shareBookingLink) return;
    try {
      await navigator.clipboard.writeText(shareBookingLink);
      toast.success('Booking link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const campDaysInvalid =
    !campSessionDatesLoading &&
    !!campSessionDates?.length &&
    !calendarEnrollmentContext &&
    campBookingMode === 'selected_days' &&
    selectedCampDays.size === 0;

  const enrollDisabled =
    isEnrolling || atCapacity || campSessionDatesLoading || campDaysInvalid;

  const pinActionsOutsideScroll = presentation === 'inline' && hideInlineCancel;
  const actionsInDrawerFooter = pinActionsOutsideScroll && !!onInlineFooterChange;
  const actionsStickyInBody = pinActionsOutsideScroll && !onInlineFooterChange;

  const calendarSessionLabel =
    calendarEnrollmentContext?.sessionDateYmd &&
    /^\d{4}-\d{2}-\d{2}$/.test(calendarEnrollmentContext.sessionDateYmd)
      ? format(
          parse(calendarEnrollmentContext.sessionDateYmd, 'yyyy-MM-dd', new Date()),
          'd MMM yyyy'
        )
      : null;

  const scopeSessionLabel =
    calendarEnrollmentContext?.kind === 'camp' ? 'This day only' : 'This session only';
  const scopeFullLabel =
    calendarEnrollmentContext?.kind === 'camp' ? 'Entire camp' : 'Entire term';
  const singleSessionScopeDisabled = !!calendarEnrollmentContext && !canEnrollSingleSession;
  const singleSessionScopeMessage =
    calendarEnrollmentContext?.kind === 'camp'
      ? 'Set a per-day price on this camp to enroll for a single day only.'
      : 'Set a per-session price on this lesson to enroll for a single session only.';

  const existingTabActions = (
    <div className="flex flex-wrap justify-end gap-2">
      {!hideInlineCancel ? (
        <Button variant="outline" onClick={onClose} disabled={isEnrolling}>
          Cancel
        </Button>
      ) : pinActionsOutsideScroll ? (
        <Button variant="outline" onClick={onClose} disabled={isEnrolling}>
          Close
        </Button>
      ) : null}
      <Button onClick={handleEnrollExisting} disabled={enrollDisabled || !selectedStudentId}>
        {isEnrolling ? 'Enrolling...' : stayOpenAfterEnroll ? 'Enroll student' : 'Enroll'}
      </Button>
    </div>
  );

  const newStudentTabActions = (
    <div className="flex flex-wrap justify-end gap-2">
      {!hideInlineCancel ? (
        <Button variant="outline" onClick={onClose} disabled={isEnrolling}>
          Cancel
        </Button>
      ) : pinActionsOutsideScroll ? (
        <Button variant="outline" onClick={onClose} disabled={isEnrolling}>
          Close
        </Button>
      ) : null}
      <Button
        onClick={handleEnrollNew}
        disabled={enrollDisabled || !newStudentFirstName || !newStudentLastName}
      >
        {isEnrolling ? 'Creating...' : stayOpenAfterEnroll ? 'Create & enroll' : 'Create & Enroll'}
      </Button>
    </div>
  );

  const drawerFooterContent = useMemo(() => {
    if (!actionsInDrawerFooter) return null;
    return activeTab === 'existing' ? existingTabActions : newStudentTabActions;
  }, [
    actionsInDrawerFooter,
    activeTab,
    isEnrolling,
    enrollDisabled,
    selectedStudentId,
    newStudentFirstName,
    newStudentLastName,
    hideInlineCancel,
    pinActionsOutsideScroll,
    stayOpenAfterEnroll,
    onClose,
  ]);

  useEffect(() => {
    if (!onInlineFooterChange) return undefined;
    onInlineFooterChange(isOpen ? drawerFooterContent : null);
    return () => onInlineFooterChange(null);
  }, [onInlineFooterChange, isOpen, drawerFooterContent]);

  const panelBody = (
    <>
        {presentation === 'inline' ? null : (
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{lessonName}</p>
            {lessonTrackLabel ? (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{lessonTrackLabel}</p>
            ) : null}
          </div>
        )}

        {shareBookingLink ? (
          <div className="mb-6 space-y-2 rounded-lg border border-purple-200 bg-purple-50/80 p-4 dark:border-purple-800 dark:bg-purple-900/20">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Share booking link</p>
            <p className="text-xs text-muted-foreground">
              Send this link so a guardian can book and pay for this private lesson online.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input type="text" readOnly value={shareBookingLink} className="min-w-0 flex-1 text-xs sm:text-sm" />
              <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1.5" onClick={() => void copyShareBookingLink()}>
                <Copy className="h-4 w-4" aria-hidden />
                Copy link
              </Button>
            </div>
          </div>
        ) : null}

        {presentation === 'inline' ? (
          <p className="mb-4 text-sm font-medium text-gray-900 dark:text-white">Add a student</p>
        ) : null}

        {showPastEnrollmentWarning ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            This lesson is in the past. You can still enroll students for record-keeping.
          </div>
        ) : null}

        {calendarEnrollmentContext && calendarSessionLabel ? (
          <div className="mb-4 space-y-3 rounded-lg border border-border bg-muted/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Enrollment scope
            </p>
            <p className="text-xs text-muted-foreground">
              Calendar date:{' '}
              <span className="font-medium text-foreground">{calendarSessionLabel}</span>
            </p>
            <div className="flex flex-col gap-2">
              <label
                className={`flex items-start gap-2 text-sm ${
                  singleSessionScopeDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                }`}
              >
                <input
                  type="radio"
                  name={`enroll-scope-${lessonId}`}
                  className="mt-0.5 text-primary"
                  checked={enrollmentScope === 'session'}
                  disabled={isEnrolling || singleSessionScopeDisabled}
                  onChange={() => setEnrollmentScope('session')}
                />
                <span>{scopeSessionLabel}</span>
              </label>
              {singleSessionScopeDisabled ? (
                <p className="text-xs text-amber-700 dark:text-amber-300">{singleSessionScopeMessage}</p>
              ) : null}
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name={`enroll-scope-${lessonId}`}
                  className="mt-0.5 text-primary"
                  checked={enrollmentScope === 'full'}
                  disabled={isEnrolling}
                  onChange={() => setEnrollmentScope('full')}
                />
                <span>{scopeFullLabel}</span>
              </label>
            </div>
          </div>
        ) : null}

        {campSessionDatesLoading ? (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading camp days…
          </div>
        ) : null}

        {!campSessionDatesLoading &&
        campSessionDates &&
        campSessionDates.length > 0 &&
        !calendarEnrollmentContext ? (
          <div className="mb-4 space-y-3 rounded-lg border border-border bg-muted/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Camp booking
            </p>
            <div className="flex flex-col gap-2">
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name={`camp-booking-mode-${lessonId}`}
                  className="mt-0.5 text-primary"
                  checked={campBookingMode === 'full_week'}
                  disabled={isEnrolling}
                  onChange={() => setCampBookingMode('full_week')}
                />
                <span>Entire week ({campSessionDates.length} days)</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="radio"
                  name={`camp-booking-mode-${lessonId}`}
                  className="mt-0.5 text-primary"
                  checked={campBookingMode === 'selected_days'}
                  disabled={isEnrolling}
                  onChange={() => setCampBookingMode('selected_days')}
                />
                <span>Selected days only</span>
              </label>
            </div>
            {campBookingMode === 'selected_days' ? (
              <div className="space-y-2 border-t border-border pt-3">
                <button
                  type="button"
                  className="text-sm font-medium text-primary hover:underline"
                  disabled={isEnrolling}
                  onClick={() => {
                    if (allCampDaysSelected) setSelectedCampDays(new Set());
                    else setSelectedCampDays(new Set(selectableCampDays));
                  }}
                >
                  {allCampDaysSelected ? 'Deselect all' : 'Select all'}
                </button>
                <div className="max-h-40 space-y-1 overflow-y-auto [scrollbar-gutter:stable]">
                  {selectableCampDays.map((dateStr) => (
                    <label
                      key={dateStr}
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary"
                        checked={selectedCampDays.has(dateStr)}
                        disabled={isEnrolling}
                        onChange={() => toggleCampDay(dateStr)}
                      />
                      <span>{formatCampDayLabel(dateStr)}</span>
                    </label>
                  ))}
                </div>
                {selectedCampDays.size === 0 ? (
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    Select at least one day to enroll.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {atCapacity ? (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
            This lesson is at capacity ({enrolledCount} / {maxCapacity}). Increase max students on the
            lesson to enroll more.
          </div>
        ) : null}

        {error && (
          <div className="mb-4 flex items-start gap-3 rounded-lg bg-red-50 p-3 text-red-700 dark:bg-red-900/20 dark:text-red-300">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-gray-800">
          <button
            type="button"
            onClick={() => setActiveTab('existing')}
            className={`px-4 pb-2 font-medium transition-colors ${
              activeTab === 'existing'
                ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Existing Student
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('new')}
            className={`px-4 pb-2 font-medium transition-colors ${
              activeTab === 'new'
                ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            New Student
          </button>
        </div>

        {activeTab === 'existing' ? (
          <div className="space-y-4">
            <div ref={searchWrapRef}>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Find student
              </label>
              <div className="relative">
                <Input
                  type="search"
                  autoComplete="off"
                  value={studentSearchQuery}
                  disabled={isEnrolling}
                  placeholder="Type name to search…"
                  className="pr-10"
                  onChange={(e) => {
                    const v = e.target.value;
                    setStudentSearchQuery(v);
                    setResultsOpen(true);
                    if (selectedStudentId && v !== selectedStudentLabel) {
                      setSelectedStudentId('');
                      setSelectedStudentLabel('');
                    }
                  }}
                  onFocus={() => setResultsOpen(true)}
                  aria-autocomplete="list"
                  aria-expanded={resultsOpen}
                  aria-controls="enroll-student-results"
                />
                {searchLoadingVisible && (
                  <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Search runs on the server as you type. Select a row to enroll.
              </p>
              {resultsOpen && studentSearchDebounced.length >= 1 && (
                <ul
                  id="enroll-student-results"
                  role="listbox"
                  className="mt-1 max-h-48 overflow-y-auto rounded-md border border-border bg-card shadow-md [scrollbar-gutter:stable]"
                >
                  {searchResults.length === 0 && !searchLoadingVisible ? (
                    <li className="px-3 py-2 text-sm text-muted-foreground">No students match.</li>
                  ) : (
                    searchResults.map((s) => (
                      <li key={s.id} role="option" aria-selected={selectedStudentId === s.id}>
                        <button
                          type="button"
                          className="flex w-full px-3 py-2 text-left text-sm hover:bg-muted"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickStudent(s)}
                        >
                          <span className="font-medium text-foreground">
                            {s.firstName} {s.lastName}
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
              {selectedStudentId ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  Selected: <span className="font-medium text-foreground">{selectedStudentLabel}</span>
                </p>
              ) : null}
            </div>

            <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Email notifications
              </p>
              <label className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 rounded border-gray-300 text-primary"
                  checked={sendBookingConfirmation}
                  disabled={isEnrolling || atCapacity}
                  onChange={(e) => setSendBookingConfirmation(e.target.checked)}
                />
                <span>Send booking confirmation email to the guardian or student</span>
              </label>
            </div>

            {!pinActionsOutsideScroll ? existingTabActions : null}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                First Name
              </label>
              <Input
                type="text"
                value={newStudentFirstName}
                onChange={(e) => setNewStudentFirstName(e.target.value)}
                placeholder="John"
                disabled={isEnrolling}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Last Name
              </label>
              <Input
                type="text"
                value={newStudentLastName}
                onChange={(e) => setNewStudentLastName(e.target.value)}
                placeholder="Doe"
                disabled={isEnrolling}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                Email <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={newStudentEmail}
                onChange={(e) => setNewStudentEmail(e.target.value)}
                placeholder="guardian@example.com"
                disabled={isEnrolling}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                If provided, we can match this student when someone registers with the same email.
              </p>
            </div>

            <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Email notifications
              </p>
              {newStudentEmail.trim() ? (
                <>
                  <label className="flex cursor-pointer items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 rounded border-gray-300 text-primary"
                      checked={sendBookingConfirmation}
                      disabled={isEnrolling || atCapacity}
                      onChange={(e) => setSendBookingConfirmation(e.target.checked)}
                    />
                    <span>Send booking confirmation email and invite to register for an account</span>
                  </label>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Add an email above to send a booking confirmation (includes account setup for new students).
                </p>
              )}
            </div>

            {!pinActionsOutsideScroll ? newStudentTabActions : null}
          </div>
        )}

        {actionsStickyInBody ? (
          <div className="-mx-4 mt-6 border-t border-gray-100 px-4 py-4 dark:border-gray-700 sm:-mx-6 sm:px-6">
            {activeTab === 'existing' ? existingTabActions : newStudentTabActions}
          </div>
        ) : null}
    </>
  );

  if (presentation === 'inline') {
    if (!isOpen) return null;
    return <div className="space-y-1">{panelBody}</div>;
  }

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Enroll Student" maxWidth="md" stackLevel="elevated">
      {panelBody}
    </Modal>
  );
}
